import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * BlackboardAdoptionIndexTool
 * -----------------------------------------------------------
 * Audits a list of Blackboard courses and generates a phase-based scorecard.
 *
 * Scorecard sections:
 * - {Phase} - Functional
 * - {Phase} - Technical
 * - {Phase} - Pedagogical
 *
 * All measured items can be manually overridden.
 * Each measured item can also be marked Applicable = Yes/No.
 * PDF export omits the Applicable column and excludes non-applicable rows.
 *
 * Course input:
 * - Paste one Blackboard courseId per line, or
 * - Upload a CSV with a "courseId" column, or
 * - Generate a CSV from Blackboard using filters and/or node associations
 */
const BlackboardAdoptionIndexTool = ({ platformUrl }) => {
    const qs = new URLSearchParams(window.location.search);
    const token = qs.get("token");

    const [courseText, setCourseText] = useState("");
    const [uploadedCourseIds, setUploadedCourseIds] = useState([]);
    const [uploadedCourseFileName, setUploadedCourseFileName] = useState("");
    const [courseFileInputKey, setCourseFileInputKey] = useState(0);

    const [discoveryTermId, setDiscoveryTermId] = useState("");
    const [discoveryDataSourceId, setDiscoveryDataSourceId] = useState("");
    const [discoveryNodeId, setDiscoveryNodeId] = useState("");
    const [discoveryCreatedDate, setDiscoveryCreatedDate] = useState("");
    const [discoveryLoading, setDiscoveryLoading] = useState(false);
    const [discoveryCount, setDiscoveryCount] = useState(0);

    const [termOptions, setTermOptions] = useState([]);
    const [dataSourceOptions, setDataSourceOptions] = useState([]);
    const [nodeOptions, setNodeOptions] = useState([]);
    const [loadingFilterOptions, setLoadingFilterOptions] = useState(false);

    const [allyClientId, setAllyClientId] = useState("");
    const [allyToken, setAllyToken] = useState("");
    const [phase, setPhase] = useState("Adoption");

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [results, setResults] = useState([]);
    const [lastRunAt, setLastRunAt] = useState("");
    const [showScorecard, setShowScorecard] = useState(false);

    const [progressCurrent, setProgressCurrent] = useState(0);
    const [progressTotal, setProgressTotal] = useState(0);
    const [currentCourse, setCurrentCourse] = useState("");

    const [scoreOverrides, setScoreOverrides] = useState({
        gradebookUsed: "",
        overallGradeConfigured: "",
        gradableItemsUsed: "",
        createdFromCopy: "",
        shellsTemplatesUsed: "",
        stagingSiteUsed: "",
        featureFlagsMaintained: "",
        illuminateEnabled: "",
        allyUsed: "",
        allyUsedInCourse: "",
        videoStudioUsed: "",
        thirdPartyToolsUsed: "",
        ihUsed: "",
        learningPlansUsed: "",
        rubricsUsed: "",
        commonCourseStructure: "",
        facultyBlackboardTrainingCompleted: "",
        facultySupportPathwaysDefined: "",
    });

    const [applicabilityOverrides, setApplicabilityOverrides] = useState({
        gradebookUsed: "Yes",
        overallGradeConfigured: "Yes",
        gradableItemsUsed: "Yes",
        createdFromCopy: "Yes",
        shellsTemplatesUsed: "Yes",
        stagingSiteUsed: "Yes",
        featureFlagsMaintained: "Yes",
        illuminateEnabled: "Yes",
        allyUsed: "Yes",
        allyUsedInCourse: "Yes",
        videoStudioUsed: "Yes",
        thirdPartyToolsUsed: "Yes",
        ihUsed: "Yes",
        learningPlansUsed: "Yes",
        rubricsUsed: "Yes",
        commonCourseStructure: "Yes",
        facultyBlackboardTrainingCompleted: "Yes",
        facultySupportPathwaysDefined: "Yes",
    });

    const authHeaders = useMemo(
        () => ({ headers: { Authorization: `Bearer ${token}` } }),
        [token]
    );

    const normalizeBase = (u) => String(u || "").trim().replace(/\/+$/, "");

    const resetAuditRunState = () => {
        setResults([]);
        setShowScorecard(false);
        setProgressCurrent(0);
        setProgressTotal(0);
        setCurrentCourse("");
        setLastRunAt("");
    };

    const makeTimestampForFile = () => {
        const d = new Date();
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const hh = String(d.getHours()).padStart(2, "0");
        const mi = String(d.getMinutes()).padStart(2, "0");
        const ss = String(d.getSeconds()).padStart(2, "0");
        return `${yyyy}${mm}${dd}-${hh}${mi}${ss}`;
    };

    const parseCsvLine = (line) => {
        const result = [];
        let current = "";
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            const next = line[i + 1];

            if (ch === '"') {
                if (inQuotes && next === '"') {
                    current += '"';
                    i++;
                } else {
                    inQuotes = !inQuotes;
                }
            } else if (ch === "," && !inQuotes) {
                result.push(current);
                current = "";
            } else {
                current += ch;
            }
        }

        result.push(current);
        return result.map((v) => v.trim());
    };

    const csvEscape = (value) => {
        const str = String(value ?? "");
        if (str.includes(",") || str.includes('"') || str.includes("\n")) {
            return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
    };

    const downloadCsv = (rows, fileName) => {
        const csvContent = rows.map((row) => row.map(csvEscape).join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const parseCourseEntries = () => {
        if (uploadedCourseIds.length > 0) {
            return uploadedCourseIds;
        }

        return String(courseText || "")
            .split(/\r?\n/)
            .map((s) => s.trim())
            .filter(Boolean);
    };

    const parseCourseCsvText = (csvText) => {
        const lines = String(csvText || "")
            .replace(/\r\n/g, "\n")
            .replace(/\r/g, "\n")
            .split("\n")
            .filter((line) => line.trim() !== "");

        if (!lines.length) {
            throw new Error("CSV file is empty");
        }

        const headers = parseCsvLine(lines[0]).map((h) => h.trim());
        const normalizedHeaders = headers.map((h) => h.toLowerCase());

        const courseIdIndex = normalizedHeaders.indexOf("courseid");
        if (courseIdIndex === -1) {
            throw new Error('CSV must include a "courseId" column');
        }

        const ids = lines
            .slice(1)
            .map((line) => parseCsvLine(line))
            .map((cols) => String(cols[courseIdIndex] || "").trim())
            .filter(Boolean);

        if (!ids.length) {
            throw new Error('CSV does not contain any values in the "courseId" column');
        }

        return Array.from(new Set(ids));
    };

    const handleCourseCsvUpload = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const csvText = e.target?.result;
                const ids = parseCourseCsvText(csvText);

                setUploadedCourseIds(ids);
                setUploadedCourseFileName(file.name);
                setDiscoveryCount(ids.length);
                setError("");
                resetAuditRunState();
            } catch (err) {
                setUploadedCourseIds([]);
                setUploadedCourseFileName("");
                setDiscoveryCount(0);
                setError(`Error parsing course CSV: ${err.message}`);
            } finally {
                setCourseFileInputKey((k) => k + 1);
            }
        };

        reader.onerror = () => {
            setUploadedCourseIds([]);
            setUploadedCourseFileName("");
            setDiscoveryCount(0);
            setError("Error reading course CSV file.");
            setCourseFileInputKey((k) => k + 1);
        };

        reader.readAsText(file);
    };

    const clearUploadedCourseCsv = () => {
        setUploadedCourseIds([]);
        setUploadedCourseFileName("");
        setDiscoveryCount(0);
        resetAuditRunState();
        setCourseFileInputKey((k) => k + 1);
    };

    const downloadCourseCsvTemplate = () => {
        const rows = [
            ["courseId"],
            ["ANALZ_TEST_001U"],
            ["BIO_101_001"],
            ["COURSE_DEMO_2026"],
        ];

        downloadCsv(rows, "blackboard-course-list-template.csv");
    };

    const downloadGeneratedCourseCsvTemplate = () => {
        const rows = [["courseId"]];
        downloadCsv(rows, "blackboard-generated-course-list-template.csv");
    };

    const clampPercent = (value) => {
        const n = Number(value);
        if (!Number.isFinite(n)) return 0;
        return Math.max(0, Math.min(100, n));
    };

    const getPaged = async (url, headersConfig = authHeaders) => {
        const base = normalizeBase(platformUrl);
        let items = [];
        let next = url;

        while (next) {
            const nextStr = String(next).trim();
            const resolvedUrl =
                nextStr.startsWith("http://") || nextStr.startsWith("https://")
                    ? nextStr
                    : `${base}${nextStr.startsWith("/") ? "" : "/"}${nextStr}`;

            const { data } = await axios.get(resolvedUrl, headersConfig);

            items = items.concat(Array.isArray(data?.results) ? data.results : []);
            next = data?.paging?.nextPage ? String(data.paging.nextPage).trim() : null;
        }

        return items;
    };

    const getCourseByCourseId = async (courseId) => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v3/courses/courseId:${encodeURIComponent(courseId)}`;
        const { data } = await axios.get(url, authHeaders);
        return data || null;
    };

    const fetchTerms = async () => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v1/terms?limit=100`;
        return await getPaged(url);
    };

    const fetchDataSources = async () => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v1/dataSources?limit=100`;
        return await getPaged(url);
    };

    const fetchInstitutionalHierarchyNodes = async () => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v1/institutionalHierarchy/nodes?limit=100`;
        return await getPaged(url);
    };

    const fetchNodeCourseAssociations = async (nodeId) => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v1/institutionalHierarchy/nodes/${encodeURIComponent(nodeId)}/courses?limit=100&expand=course`;
        return await getPaged(url);
    };

    const getTermValue = (term) =>
        String(term?.id || term?.termId || "").trim();

    const getTermLabel = (term) => {
        const name =
            String(term?.name || term?.title || term?.externalId || term?.id || "").trim();
        const value = getTermValue(term);
        return value && name && value !== name ? `${name} (${value})` : name || value;
    };

    const getDataSourceValue = (ds) =>
        String(ds?.id || "").trim();

    const getDataSourceLabel = (ds) => {
        const name =
            String(ds?.externalId || ds?.name || ds?.description || ds?.id || "").trim();
        const value = getDataSourceValue(ds);
        return value && name && value !== name ? `${name} (${value})` : name || value;
    };

    const getNodeValue = (node) =>
        String(node?.id || node?.nodeId || "").trim();

    const getNodeLabel = (node) => {
        const name =
            String(node?.name || node?.title || node?.externalId || node?.id || "").trim();
        const value = getNodeValue(node);
        return value && name && value !== name ? `${name} (${value})` : name || value;
    };

    const getSelectedTermLabel = () => {
        if (!discoveryTermId) return "All Terms";
        const match = termOptions.find((term) => getTermValue(term) === discoveryTermId);
        return match ? getTermLabel(match) : discoveryTermId;
    };

    const getSelectedDataSourceLabel = () => {
        if (!discoveryDataSourceId) return "All Data Sources";
        const match = dataSourceOptions.find((ds) => getDataSourceValue(ds) === discoveryDataSourceId);
        return match ? getDataSourceLabel(match) : discoveryDataSourceId;
    };

    const getSelectedNodeLabel = () => {
        if (!discoveryNodeId) return "All Nodes";
        const match = nodeOptions.find((node) => getNodeValue(node) === discoveryNodeId);
        return match ? getNodeLabel(match) : discoveryNodeId;
    };

    const extractCourseIdFromNodeAssociation = (row) => {
        return String(
            row?.course?.courseId ||
            row?.courseId ||
            row?.course?.externalId ||
            row?.course?.id ||
            ""
        ).trim();
    };

    useEffect(() => {
        if (!token || !platformUrl) return;

        let cancelled = false;

        const loadFilterOptions = async () => {
            try {
                setLoadingFilterOptions(true);

                const [terms, dataSources, nodes] = await Promise.all([
                    fetchTerms(),
                    fetchDataSources(),
                    fetchInstitutionalHierarchyNodes(),
                ]);

                if (cancelled) return;

                setTermOptions(Array.isArray(terms) ? terms : []);
                setDataSourceOptions(Array.isArray(dataSources) ? dataSources : []);
                setNodeOptions(Array.isArray(nodes) ? nodes : []);
            } catch (e) {
                if (cancelled) return;
                setError(
                    e?.response?.data?.message ||
                    e?.message ||
                    "Failed to load term/data source/node options."
                );
            } finally {
                if (!cancelled) {
                    setLoadingFilterOptions(false);
                }
            }
        };

        loadFilterOptions();

        return () => {
            cancelled = true;
        };
    }, [token, platformUrl]);

    const fetchFilteredCoursesForCsv = async () => {
        const base = normalizeBase(platformUrl);
        const query = new URLSearchParams();
        query.set("limit", "100");

        if (discoveryTermId.trim()) {
            query.set("termId", discoveryTermId.trim());
        }

        if (discoveryDataSourceId.trim()) {
            query.set("dataSourceId", discoveryDataSourceId.trim());
        }

        if (discoveryCreatedDate.trim()) {
            query.set("createdCompare", "greaterOrEqual");
            query.set("created", discoveryCreatedDate.trim());
        }

        const url = `${base}/learn/api/public/v3/courses?${query.toString()}`;
        return await getPaged(url);
    };

    const handleGenerateCourseCsv = async () => {
        if (!token) {
            setError("Missing token in URL query string.");
            return;
        }

        if (
            !discoveryTermId.trim() &&
            !discoveryDataSourceId.trim() &&
            !discoveryNodeId.trim() &&
            !discoveryCreatedDate.trim()
        ) {
            setError("Enter at least one filter: term, data source, node, or created date.");
            return;
        }

        try {
            setDiscoveryLoading(true);
            setError("");

            let ids = [];

            const hasNodeFilter = !!discoveryNodeId.trim();
            const hasCourseFilters =
                !!discoveryTermId.trim() ||
                !!discoveryDataSourceId.trim() ||
                !!discoveryCreatedDate.trim();

            if (hasNodeFilter) {
                const nodeAssociations = await fetchNodeCourseAssociations(discoveryNodeId.trim());

                const nodeIds = Array.from(
                    new Set(
                        nodeAssociations
                            .map(extractCourseIdFromNodeAssociation)
                            .filter(Boolean)
                    )
                );

                if (hasCourseFilters) {
                    const filteredCourses = await fetchFilteredCoursesForCsv();
                    const filteredIds = new Set(
                        filteredCourses
                            .map((c) => String(c?.courseId || "").trim())
                            .filter(Boolean)
                    );

                    ids = nodeIds.filter((id) => filteredIds.has(id));
                } else {
                    ids = nodeIds;
                }
            } else {
                const courses = await fetchFilteredCoursesForCsv();
                ids = Array.from(
                    new Set(
                        courses
                            .map((c) => String(c?.courseId || "").trim())
                            .filter(Boolean)
                    )
                );
            }

            ids = ids.sort((a, b) => a.localeCompare(b));

            if (!ids.length) {
                setUploadedCourseIds([]);
                setUploadedCourseFileName("");
                setDiscoveryCount(0);
                resetAuditRunState();
                setError("No courses found for the selected filters.");
                return;
            }

            const rows = [["courseId"], ...ids.map((id) => [id])];
            const fileName = `blackboard-course-list-${makeTimestampForFile()}.csv`;

            downloadCsv(rows, fileName);

            setUploadedCourseIds(ids);
            setUploadedCourseFileName(fileName);
            setDiscoveryCount(ids.length);
            setCourseText("");
            resetAuditRunState();
        } catch (e) {
            setUploadedCourseIds([]);
            setUploadedCourseFileName("");
            setDiscoveryCount(0);
            resetAuditRunState();
            setError(
                e?.response?.data?.message ||
                e?.message ||
                "Failed to generate course CSV."
            );
        } finally {
            setDiscoveryLoading(false);
        }
    };

    const fetchGradebookColumns = async (courseId) => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v2/courses/courseId:${encodeURIComponent(courseId)}/gradebook/columns?limit=100`;
        return await getPaged(url);
    };

    const fetchRootContents = async (courseId) => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v1/courses/courseId:${encodeURIComponent(courseId)}/contents?limit=100`;
        return await getPaged(url);
    };

    const fetchChildren = async (courseId, contentId) => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v1/courses/courseId:${encodeURIComponent(courseId)}/contents/${encodeURIComponent(contentId)}/children?limit=100`;
        return await getPaged(url);
    };

    const fetchCourseRubrics = async (courseId) => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v1/courses/courseId:${encodeURIComponent(courseId)}/rubrics?limit=100`;
        return await getPaged(url);
    };

    const fetchRubricAssociations = async (courseId, rubricId) => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v1/courses/courseId:${encodeURIComponent(courseId)}/rubrics/${encodeURIComponent(rubricId)}/associations?limit=100`;
        return await getPaged(url);
    };

    const fetchLtiPlacements = async () => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v1/lti/placements?limit=100`;
        return await getPaged(url);
    };

    const fetchAllyOverallReport = async (clientId, bearerToken) => {
        if (!clientId || !bearerToken) {
            return [];
        }

        const url = `https://prod.ally.ac/api/v2/clients/${encodeURIComponent(clientId)}/reports/overall`;
        const headersConfig = {
            headers: {
                Authorization: `Bearer ${bearerToken}`,
            },
        };

        const { data } = await axios.get(url, headersConfig);
        return Array.isArray(data?.data) ? data.data : [];
    };

    const GRADEABLE_SCORE_PROVIDER_HANDLES = new Set([
        "resource/x-bb-assessment",
        "resource/x-bb-forumlink",
    ]);

    const isGradableColumn = (col) => {
        const handle = String(col?.scoreProviderHandle || "").trim().toLowerCase();
        return GRADEABLE_SCORE_PROVIDER_HANDLES.has(handle);
    };

    const isContainerHandler = (handlerId = "") => {
        const id = String(handlerId || "").toLowerCase();
        return (
            id.includes("folder") ||
            id.includes("learningmodule") ||
            id.includes("lesson") ||
            id.includes("module")
        );
    };

    const isLessonPlanItem = (item) => {
        const handlerId = String(item?.contentHandler?.id || "").toLowerCase();
        const title = String(item?.title || "").toLowerCase();
        return handlerId.includes("lesson") || title.includes("lesson plan");
    };

    const isVideoStudioItem = (item) => {
        const handlerId = String(item?.contentHandler?.id || "").trim().toLowerCase();
        return handlerId === "resource/x-bb-video-studio";
    };

    const isThirdPartyToolItem = (item) => {
        const handlerId = String(item?.contentHandler?.id || "").trim().toLowerCase();
        return (
            handlerId === "resource/x-bb-bltiplacement" ||
            handlerId === "resource/x-bb-blti-link"
        );
    };

    const wasCreatedFromCopy = (course) => {
        return Array.isArray(course?.copyHistory) && course.copyHistory.length > 0;
    };

    const isAllyPlacementAvailable = (placement) => {
        const name = String(placement?.name || "").trim();
        const available = String(placement?.availability?.available || "").trim().toLowerCase();
        return name === "Ally Institution Report LTI 1.3" && available === "yes";
    };

    const buildAllyCourseMap = (allyRows) => {
        const map = new Map();

        for (const row of allyRows || []) {
            const courseId = String(row?.courseId || "").trim();
            if (!courseId) continue;
            map.set(courseId, row);
        }

        return map;
    };

    const hasAssociatedRubrics = async (courseId, rubrics) => {
        if (!Array.isArray(rubrics) || rubrics.length === 0) {
            return false;
        }

        for (const rubric of rubrics) {
            const rubricId = String(rubric?.id || "").trim();
            if (!rubricId) continue;

            try {
                const associations = await fetchRubricAssociations(courseId, rubricId);
                if (Array.isArray(associations) && associations.length > 0) {
                    return true;
                }
            } catch (_e) {
                // ignore rubric-level association failures and continue
            }
        }

        return false;
    };

    const crawlCourseContents = async (courseId) => {
        const all = [];
        const queue = [];

        const rootChildren = await fetchRootContents(courseId);
        for (const ch of rootChildren) {
            all.push({ ...ch, parentId: "ROOT" });
            queue.push({
                id: ch.id,
                handlerId: ch?.contentHandler?.id || "",
                hasChildren: !!ch?.hasChildren,
            });
        }

        while (queue.length > 0) {
            const node = queue.shift();
            if (!node) continue;

            if (!(node.hasChildren || isContainerHandler(node.handlerId))) {
                continue;
            }

            try {
                const kids = await fetchChildren(courseId, node.id);
                for (const k of kids) {
                    all.push({ ...k, parentId: node.id });
                    queue.push({
                        id: k.id,
                        handlerId: k?.contentHandler?.id || "",
                        hasChildren: !!k?.hasChildren,
                    });
                }
            } catch (_e) {
                // Ignore node-level failures and continue
            }
        }

        return all;
    };

    const buildAuditRow = async (entry, sharedChecks) => {
        try {
            const course = await getCourseByCourseId(entry);

            if (!course?.id) {
                return {
                    input: entry,
                    courseId: "",
                    courseName: "",
                    courseUuid: "",
                    hasOverallGrade: "",
                    gradableColumnCount: "",
                    usesLessonPlans: "",
                    videoStudioUsed: "",
                    thirdPartyToolsUsed: "",
                    rubricsUsed: "",
                    createdFromCopy: "",
                    ihUsed: "",
                    allyUsed: "",
                    allyUsedInCourse: "",
                    contentItemCount: "",
                    status: "Course not found",
                };
            }

            const courseId = course?.courseId || entry;
            const coursePkId = String(course?.id || "").trim();
            const courseUuid = course?.uuid || "";

            const [columns, contents, rubrics] = await Promise.all([
                fetchGradebookColumns(courseId),
                crawlCourseContents(courseId),
                fetchCourseRubrics(courseId),
            ]);

            const overallGradeColumn = columns.find(
                (c) => String(c?.name || c?.title || "").trim().toLowerCase() === "overall grade"
            );

            const gradableColumns = columns.filter(isGradableColumn);
            const lessonPlanCount = contents.filter(isLessonPlanItem).length;
            const videoStudioCount = contents.filter(isVideoStudioItem).length;
            const thirdPartyToolCount = contents.filter(isThirdPartyToolItem).length;
            const rubricsUsed = await hasAssociatedRubrics(courseId, rubrics);
            const createdFromCopy = wasCreatedFromCopy(course);

            const allyCourseRow = sharedChecks.allyCourseMap.get(coursePkId);
            const allyUsedInCourse = allyCourseRow?.allyEnabled === true;

            return {
                input: entry,
                courseId: course?.courseId || "",
                courseName: course?.name || "",
                courseUuid,
                hasOverallGrade: overallGradeColumn ? "Yes" : "No",
                gradableColumnCount: gradableColumns.length,
                usesLessonPlans: lessonPlanCount > 0 ? "Yes" : "No",
                videoStudioUsed: videoStudioCount > 0 ? "Yes" : "No",
                thirdPartyToolsUsed: thirdPartyToolCount > 0 ? "Yes" : "No",
                rubricsUsed: rubricsUsed ? "Yes" : "No",
                createdFromCopy: createdFromCopy ? "Yes" : "No",
                ihUsed: sharedChecks.ihUsed ? "Yes" : "No",
                allyUsed: sharedChecks.allyUsed ? "Yes" : "No",
                allyUsedInCourse: allyUsedInCourse ? "Yes" : "No",
                contentItemCount: contents.length,
                status: "OK",
            };
        } catch (e) {
            return {
                input: entry,
                courseId: "",
                courseName: "",
                courseUuid: "",
                hasOverallGrade: "",
                gradableColumnCount: "",
                usesLessonPlans: "",
                videoStudioUsed: "",
                thirdPartyToolsUsed: "",
                rubricsUsed: "",
                createdFromCopy: "",
                ihUsed: "",
                allyUsed: "",
                allyUsedInCourse: "",
                contentItemCount: "",
                status: e?.response?.data?.message || e?.message || "Failed to audit course",
            };
        }
    };

    const mapWithLimit = async (arr, limit, fn) => {
        const out = new Array(arr.length);
        let idx = 0;
        let completed = 0;

        const workers = new Array(Math.min(limit, arr.length)).fill(0).map(async () => {
            while (idx < arr.length) {
                const cur = idx++;
                const entry = arr[cur];

                setCurrentCourse(entry);
                setProgressCurrent(completed);

                out[cur] = await fn(entry, cur);

                completed += 1;
                setProgressCurrent(completed);
            }
        });

        await Promise.all(workers);
        return out;
    };

    const runAudit = async () => {
        if (!token) {
            setError("Missing token in URL query string.");
            return;
        }

        const entries = parseCourseEntries();
        if (!entries.length) {
            setError("Enter at least one courseId or upload or generate a course CSV.");
            return;
        }

        try {
            setLoading(true);
            setError("");
            setResults([]);
            setShowScorecard(false);
            setProgressCurrent(0);
            setProgressTotal(entries.length);
            setCurrentCourse(entries[0] || "");

            const [ihNodes, ltiPlacements, allyOverallRows] = await Promise.all([
                fetchInstitutionalHierarchyNodes(),
                fetchLtiPlacements(),
                fetchAllyOverallReport(allyClientId, allyToken),
            ]);

            const sharedChecks = {
                ihUsed: Array.isArray(ihNodes) && ihNodes.length > 0,
                allyUsed: Array.isArray(ltiPlacements) && ltiPlacements.some(isAllyPlacementAvailable),
                allyCourseMap: buildAllyCourseMap(allyOverallRows),
            };

            const rows = await mapWithLimit(entries, 4, (entry) => buildAuditRow(entry, sharedChecks));
            setResults(rows);
            setLastRunAt(new Date().toLocaleString());
            setCurrentCourse("");
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || "Audit failed.");
        } finally {
            setLoading(false);
            setCurrentCourse("");
        }
    };

    const summary = useMemo(() => {
        const okRows = results.filter((r) => r.status === "OK");
        return {
            total: results.length,
            resolved: okRows.length,
            withOverallGrade: okRows.filter((r) => r.hasOverallGrade === "Yes").length,
            withLessonPlans: okRows.filter((r) => r.usesLessonPlans === "Yes").length,
            withVideoStudio: okRows.filter((r) => r.videoStudioUsed === "Yes").length,
            withThirdPartyTools: okRows.filter((r) => r.thirdPartyToolsUsed === "Yes").length,
            withRubrics: okRows.filter((r) => r.rubricsUsed === "Yes").length,
            withGradableItems: okRows.filter((r) => Number(r.gradableColumnCount) > 0).length,
            createdFromCopyCount: okRows.filter((r) => r.createdFromCopy === "Yes").length,
            ihUsedCount: okRows.filter((r) => r.ihUsed === "Yes").length,
            allyUsedCount: okRows.filter((r) => r.allyUsed === "Yes").length,
            allyUsedInCourseCount: okRows.filter((r) => r.allyUsedInCourse === "Yes").length,
            totalContentItems: okRows.reduce((sum, r) => sum + (Number(r.contentItemCount) || 0), 0),
        };
    }, [results]);

    const assessedCourses = useMemo(
        () => results.filter((r) => r.status === "OK"),
        [results]
    );

    const totalAssessed = assessedCourses.length;

    const autoPercent = (count) => {
        if (!totalAssessed) return 0;
        return (count / totalAssessed) * 100;
    };

    const getScoreFromPercent = (percentage) => {
        if (percentage === 0) return 0;
        if (percentage <= 50) return 1;
        if (percentage <= 75) return 2;
        return 3;
    };

    const scorecardSections = useMemo(() => {
        const functionalSection = `${phase} - Functional`;
        const technicalSection = `${phase} - Technical`;
        const pedagogicalSection = `${phase} - Pedagogical`;

        const items = [
            {
                section: functionalSection,
                key: "gradebookUsed",
                label: "Gradebook Used",
                autoPercentage: 0,
                autoNumerator: null,
                autoDenominator: totalAssessed,
                autoComputed: false,
            },
            {
                section: functionalSection,
                key: "overallGradeConfigured",
                label: "Overall Grade Configured",
                autoPercentage: autoPercent(summary.withOverallGrade),
                autoNumerator: summary.withOverallGrade,
                autoDenominator: totalAssessed,
                autoComputed: true,
            },
            {
                section: functionalSection,
                key: "gradableItemsUsed",
                label: "Gradable Items Used",
                autoPercentage: autoPercent(summary.withGradableItems),
                autoNumerator: summary.withGradableItems,
                autoDenominator: totalAssessed,
                autoComputed: true,
            },
            {
                section: functionalSection,
                key: "createdFromCopy",
                label: "Created from Copy",
                autoPercentage: autoPercent(summary.createdFromCopyCount),
                autoNumerator: summary.createdFromCopyCount,
                autoDenominator: totalAssessed,
                autoComputed: true,
            },
            {
                section: functionalSection,
                key: "shellsTemplatesUsed",
                label: "Shells/Templates Used",
                autoPercentage: 0,
                autoNumerator: null,
                autoDenominator: totalAssessed,
                autoComputed: false,
            },

            {
                section: technicalSection,
                key: "allyUsed",
                label: "Ally Enabled",
                autoPercentage: autoPercent(summary.allyUsedCount),
                autoNumerator: summary.allyUsedCount,
                autoDenominator: totalAssessed,
                autoComputed: true,
            },
            {
                section: technicalSection,
                key: "allyUsedInCourse",
                label: "Ally Used in Course",
                autoPercentage: autoPercent(summary.allyUsedInCourseCount),
                autoNumerator: summary.allyUsedInCourseCount,
                autoDenominator: totalAssessed,
                autoComputed: true,
            },
            {
                section: technicalSection,
                key: "videoStudioUsed",
                label: "Video Studio Used",
                autoPercentage: autoPercent(summary.withVideoStudio),
                autoNumerator: summary.withVideoStudio,
                autoDenominator: totalAssessed,
                autoComputed: true,
            },
            {
                section: technicalSection,
                key: "thirdPartyToolsUsed",
                label: "Third Party Tools Used",
                autoPercentage: autoPercent(summary.withThirdPartyTools),
                autoNumerator: summary.withThirdPartyTools,
                autoDenominator: totalAssessed,
                autoComputed: true,
            },
            {
                section: technicalSection,
                key: "ihUsed",
                label: "IH Used",
                autoPercentage: autoPercent(summary.ihUsedCount),
                autoNumerator: summary.ihUsedCount,
                autoDenominator: totalAssessed,
                autoComputed: true,
            },
            {
                section: technicalSection,
                key: "stagingSiteUsed",
                label: "Staging Site Used",
                autoPercentage: 0,
                autoNumerator: null,
                autoDenominator: totalAssessed,
                autoComputed: false,
            },
            {
                section: technicalSection,
                key: "featureFlagsMaintained",
                label: "Feature Flags Maintained",
                autoPercentage: 0,
                autoNumerator: null,
                autoDenominator: totalAssessed,
                autoComputed: false,
            },
            {
                section: technicalSection,
                key: "illuminateEnabled",
                label: "Illuminate Enabled",
                autoPercentage: 0,
                autoNumerator: null,
                autoDenominator: totalAssessed,
                autoComputed: false,
            },

            {
                section: pedagogicalSection,
                key: "rubricsUsed",
                label: "Rubrics Used",
                autoPercentage: autoPercent(summary.withRubrics),
                autoNumerator: summary.withRubrics,
                autoDenominator: totalAssessed,
                autoComputed: true,
            },
            {
                section: pedagogicalSection,
                key: "learningPlansUsed",
                label: "Learning Plans Used",
                autoPercentage: autoPercent(summary.withLessonPlans),
                autoNumerator: summary.withLessonPlans,
                autoDenominator: totalAssessed,
                autoComputed: true,
            },
            {
                section: pedagogicalSection,
                key: "commonCourseStructure",
                label: "Course Organization Follows Common Structure",
                autoPercentage: 0,
                autoNumerator: null,
                autoDenominator: totalAssessed,
                autoComputed: false,
            },
            {
                section: pedagogicalSection,
                key: "facultyBlackboardTrainingCompleted",
                label: "Faculty Blackboard Training Completed",
                autoPercentage: 0,
                autoNumerator: null,
                autoDenominator: totalAssessed,
                autoComputed: false,
            },
            {
                section: pedagogicalSection,
                key: "facultySupportPathwaysDefined",
                label: "Faculty Support Pathways Defined",
                autoPercentage: 0,
                autoNumerator: null,
                autoDenominator: totalAssessed,
                autoComputed: false,
            },
        ];

        return items.map((item) => {
            const overrideRaw = scoreOverrides[item.key];
            const hasOverride = overrideRaw !== "" && overrideRaw !== null && overrideRaw !== undefined;
            const effectivePercentage = hasOverride
                ? clampPercent(overrideRaw)
                : clampPercent(item.autoPercentage);

            const applicableRaw = applicabilityOverrides[item.key];
            const applicable = applicableRaw === "No" ? "No" : "Yes";

            return {
                ...item,
                overrideValue: overrideRaw,
                hasOverride,
                effectivePercentage,
                effectiveScore: getScoreFromPercent(effectivePercentage),
                applicable,
            };
        });
    }, [phase, scoreOverrides, applicabilityOverrides, summary, totalAssessed]);

    const groupedScorecardSections = useMemo(() => {
        return scorecardSections.reduce((acc, item) => {
            (acc[item.section] ||= []).push(item);
            return acc;
        }, {});
    }, [scorecardSections]);

    const orderedSectionNames = useMemo(() => {
        const names = [
            `${phase} - Functional`,
            `${phase} - Technical`,
            `${phase} - Pedagogical`,
        ];
        return names.filter((name) => groupedScorecardSections[name]?.length);
    }, [phase, groupedScorecardSections]);

    const handleOverrideChange = (key, value) => {
        if (value === "") {
            setScoreOverrides((prev) => ({ ...prev, [key]: "" }));
            return;
        }

        const n = Number(value);
        if (!Number.isFinite(n)) {
            return;
        }

        setScoreOverrides((prev) => ({
            ...prev,
            [key]: String(clampPercent(n)),
        }));
    };

    const handleApplicabilityChange = (key, value) => {
        setApplicabilityOverrides((prev) => ({
            ...prev,
            [key]: value === "No" ? "No" : "Yes",
        }));
    };

    const downloadScorecardPdf = () => {
        if (!scorecardSections.length || totalAssessed === 0) return;

        const pdfSectionData = orderedSectionNames
            .map((sectionName) => ({
                sectionName,
                rows: (groupedScorecardSections[sectionName] || []).filter(
                    (row) => row.applicable === "Yes"
                ),
            }))
            .filter((section) => section.rows.length > 0);

        if (!pdfSectionData.length) return;

        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
        const generatedAt = new Date().toLocaleString();
        const left = 24;

        const learnUrl = normalizeBase(platformUrl);
        const selectedTermLabel = getSelectedTermLabel();
        const selectedDataSourceLabel = getSelectedDataSourceLabel();
        const selectedNodeLabel = getSelectedNodeLabel();
        const selectedCreatedDate = discoveryCreatedDate || "Not specified";

        const drawSectionTable = (sectionName, rows, startY) => {
            doc.setFontSize(14);
            doc.text(sectionName, left, startY);

            autoTable(doc, {
                startY: startY + 8,
                margin: { left: 24, right: 24 },
                theme: "grid",
                head: [[
                    "Measured Item",
                    "Auto Value",
                    "Override %",
                    "Effective %",
                    "0 — Not in Place",
                    "1 — Inconsistent",
                    "2 — Mostly Consistent",
                    "3 — Standardized",
                ]],
                body: rows.map((row) => [
                    row.label,
                    row.autoComputed
                        ? `${row.autoNumerator} / ${row.autoDenominator} (${row.autoPercentage.toFixed(1)}%)`
                        : "Manual only",
                    row.hasOverride ? `${clampPercent(row.overrideValue).toFixed(1)}%` : "",
                    `${row.effectivePercentage.toFixed(1)}%`,
                    row.effectiveScore === 0 ? "X" : "",
                    row.effectiveScore === 1 ? "X" : "",
                    row.effectiveScore === 2 ? "X" : "",
                    row.effectiveScore === 3 ? "X" : "",
                ]),
                styles: {
                    fontSize: 7,
                    cellPadding: 5,
                    overflow: "linebreak",
                    valign: "middle",
                    textColor: [17, 24, 39],
                    lineColor: [209, 213, 219],
                    lineWidth: 1,
                },
                headStyles: {
                    fillColor: [21, 94, 117],
                    textColor: [255, 255, 255],
                    fontStyle: "bold",
                    halign: "center",
                    valign: "middle",
                },
                bodyStyles: {
                    fillColor: [255, 255, 255],
                },
                columnStyles: {
                    0: { cellWidth: 125 },
                    1: { cellWidth: 120 },
                    2: { cellWidth: 58, halign: "center" },
                    3: { cellWidth: 58, halign: "center" },
                    4: { cellWidth: 82, halign: "center" },
                    5: { cellWidth: 82, halign: "center" },
                    6: { cellWidth: 96, halign: "center" },
                    7: { cellWidth: 88, halign: "center" },
                },
                didParseCell: (data) => {
                    if (data.section === "body" && data.column.index >= 4) {
                        data.cell.styles.halign = "center";
                        data.cell.styles.fontStyle = "bold";
                    }
                },
            });

            return doc.lastAutoTable.finalY + 20;
        };

        doc.setFontSize(18);
        doc.text(`Blackboard ${phase} Scorecard`, left, 36);

        doc.setFontSize(10);
        doc.text(`Learn URL: ${learnUrl}`, left, 54);
        doc.text(`Phase: ${phase}`, left, 68);
        doc.text(`Generated: ${generatedAt}`, left, 82);
        doc.text(`Assessed Courses: ${totalAssessed}`, left, 96);

        doc.text(`CSV Criteria - Term: ${selectedTermLabel}`, left, 116);
        doc.text(`CSV Criteria - Data Source: ${selectedDataSourceLabel}`, left, 130);
        doc.text(`CSV Criteria - Node: ${selectedNodeLabel}`, left, 144);
        doc.text(`CSV Criteria - Created On/After: ${selectedCreatedDate}`, left, 158);

        let currentY = 182;

        pdfSectionData.forEach((section, index) => {
            if (index > 0 && currentY > 500) {
                doc.addPage("letter", "landscape");
                currentY = 40;
            }

            currentY = drawSectionTable(section.sectionName, section.rows, currentY);
        });

        doc.save(`blackboard-${phase.toLowerCase()}-scorecard.pdf`);
    };

    const progressPercent =
        progressTotal > 0 ? Math.round((progressCurrent / progressTotal) * 100) : 0;

    return (
        <div className="bb-container">
            <div
                style={{
                    border: "1px solid #d1d5db",
                    borderRadius: 8,
                    padding: 16,
                    background: "#fff",
                    marginBottom: 16,
                }}
            >
                <div style={{ marginBottom: 12, fontWeight: 700, fontSize: 18 }}>
                    Step 1: Generate Course CSV
                </div>
                <div style={{ marginBottom: 8, fontSize: 13, opacity: 0.85 }}>
                    Enter one or more filters, then generate a CSV containing only courseIds.
                </div>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr 1fr",
                        gap: 12,
                        marginBottom: 12,
                    }}
                >
                    <div>
                        <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 600 }}>
                            Term
                        </div>
                        <select
                            value={discoveryTermId}
                            onChange={(e) => setDiscoveryTermId(e.target.value)}
                            disabled={loadingFilterOptions}
                            style={{
                                width: "100%",
                                boxSizing: "border-box",
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                padding: 10,
                                fontSize: 14,
                                background: "#fff",
                            }}
                        >
                            <option value="">All Terms</option>
                            {termOptions.map((term) => {
                                const value = getTermValue(term);
                                if (!value) return null;
                                return (
                                    <option key={value} value={value}>
                                        {getTermLabel(term)}
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    <div>
                        <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 600 }}>
                            Data Source
                        </div>
                        <select
                            value={discoveryDataSourceId}
                            onChange={(e) => setDiscoveryDataSourceId(e.target.value)}
                            disabled={loadingFilterOptions}
                            style={{
                                width: "100%",
                                boxSizing: "border-box",
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                padding: 10,
                                fontSize: 14,
                                background: "#fff",
                            }}
                        >
                            <option value="">All Data Sources</option>
                            {dataSourceOptions.map((ds) => {
                                const value = getDataSourceValue(ds);
                                if (!value) return null;
                                return (
                                    <option key={value} value={value}>
                                        {getDataSourceLabel(ds)}
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    <div>
                        <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 600 }}>
                            Node
                        </div>
                        <select
                            value={discoveryNodeId}
                            onChange={(e) => setDiscoveryNodeId(e.target.value)}
                            disabled={loadingFilterOptions}
                            style={{
                                width: "100%",
                                boxSizing: "border-box",
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                padding: 10,
                                fontSize: 14,
                                background: "#fff",
                            }}
                        >
                            <option value="">All Nodes</option>
                            {nodeOptions.map((node) => {
                                const value = getNodeValue(node);
                                if (!value) return null;
                                return (
                                    <option key={value} value={value}>
                                        {getNodeLabel(node)}
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    <div>
                        <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 600 }}>
                            Created On/After
                        </div>
                        <input
                            type="date"
                            value={discoveryCreatedDate}
                            onChange={(e) => setDiscoveryCreatedDate(e.target.value)}
                            style={{
                                width: "100%",
                                boxSizing: "border-box",
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                padding: 10,
                                fontSize: 14,
                            }}
                        />
                    </div>
                </div>

                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
                    <button
                        type="button"
                        className="bb-btn"
                        onClick={handleGenerateCourseCsv}
                        disabled={discoveryLoading}
                    >
                        {discoveryLoading ? "Generating CSV..." : "Generate Course CSV"}
                    </button>

                    <button
                        type="button"
                        className="bb-btn"
                        onClick={downloadGeneratedCourseCsvTemplate}
                    >
                        Download Empty CSV Template
                    </button>

                    {loadingFilterOptions ? (
                        <span style={{ fontSize: 13, color: "#6b7280" }}>
                            Loading term/data source/node options...
                        </span>
                    ) : null}

                    {discoveryCount > 0 ? (
                        <span style={{ fontSize: 13, color: "#374151" }}>
                            Found {discoveryCount} course{discoveryCount === 1 ? "" : "s"}.
                        </span>
                    ) : null}
                </div>
            </div>

            <div
                style={{
                    border: "1px solid #d1d5db",
                    borderRadius: 8,
                    padding: 16,
                    background: "#fff",
                    marginBottom: 16,
                }}
            >
                <div style={{ marginBottom: 8, fontWeight: 700, fontSize: 18 }}>Step 2: Run Audit</div>
                <div style={{ marginBottom: 8, fontSize: 13, opacity: 0.85 }}>
                    Paste one Blackboard courseId per line, upload a CSV with a <code>courseId</code> column,
                    or use the generated CSV from Step 1.
                </div>

                <textarea
                    value={courseText}
                    onChange={(e) => setCourseText(e.target.value)}
                    rows={8}
                    placeholder={`ANALZ_TEST_001U
BIO_101_001
COURSE_DEMO_2026`}
                    style={{
                        width: "100%",
                        boxSizing: "border-box",
                        border: "1px solid #d1d5db",
                        borderRadius: 8,
                        padding: 12,
                        fontFamily: "inherit",
                        fontSize: 14,
                        marginBottom: 12,
                    }}
                />

                <div
                    style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 8,
                        padding: 12,
                        marginBottom: 12,
                        background: "#f9fafb",
                    }}
                >
                    <div style={{ marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                        Upload Course CSV
                    </div>

                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <input
                            key={courseFileInputKey}
                            type="file"
                            accept=".csv,text/csv"
                            onChange={handleCourseCsvUpload}
                        />
                        <button type="button" className="bb-btn" onClick={downloadCourseCsvTemplate}>
                            Download Sample CSV Template
                        </button>
                        {uploadedCourseIds.length > 0 ? (
                            <button type="button" className="bb-btn" onClick={clearUploadedCourseCsv}>
                                Clear Uploaded CSV
                            </button>
                        ) : null}
                    </div>

                    {uploadedCourseIds.length > 0 ? (
                        <div style={{ marginTop: 8, fontSize: 13, color: "#374151" }}>
                            Using course CSV: <strong>{uploadedCourseFileName}</strong> ({uploadedCourseIds.length} course{uploadedCourseIds.length === 1 ? "" : "s"})
                        </div>
                    ) : (
                        <div style={{ marginTop: 8, fontSize: 13, color: "#6b7280" }}>
                            No CSV uploaded. The textarea will be used.
                        </div>
                    )}
                </div>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: 12,
                        marginBottom: 12,
                    }}
                >
                    <div>
                        <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 600 }}>
                            Phase
                        </div>
                        <select
                            value={phase}
                            onChange={(e) => setPhase(e.target.value)}
                            style={{
                                width: "100%",
                                boxSizing: "border-box",
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                padding: 10,
                                fontSize: 14,
                                background: "#fff",
                            }}
                        >
                            <option value="Adoption">Adoption</option>
                            <option value="Optimization">Optimization</option>
                            <option value="Transformation">Transformation</option>
                        </select>
                    </div>

                    <div>
                        <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 600 }}>
                            Ally Client ID
                        </div>
                        <input
                            type="text"
                            value={allyClientId}
                            onChange={(e) => setAllyClientId(e.target.value)}
                            placeholder="15364"
                            style={{
                                width: "100%",
                                boxSizing: "border-box",
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                padding: 10,
                                fontSize: 14,
                            }}
                        />
                    </div>

                    <div>
                        <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 600 }}>
                            Ally Token
                        </div>
                        <input
                            type="password"
                            value={allyToken}
                            onChange={(e) => setAllyToken(e.target.value)}
                            placeholder="Bearer token for Ally API"
                            style={{
                                width: "100%",
                                boxSizing: "border-box",
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                padding: 10,
                                fontSize: 14,
                            }}
                        />
                    </div>
                </div>

                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <button className="bb-btn" onClick={runAudit} disabled={loading}>
                        {loading ? "Checking..." : "Check Courses"}
                    </button>
                    <button
                        className="bb-btn"
                        onClick={() => setShowScorecard((s) => !s)}
                        disabled={results.length === 0}
                    >
                        {showScorecard ? "Hide Scorecard" : "Show Scorecard"}
                    </button>
                    <button
                        className="bb-btn"
                        onClick={downloadScorecardPdf}
                        disabled={totalAssessed === 0}
                    >
                        Download Scorecard PDF
                    </button>
                    {lastRunAt ? (
                        <span style={{ fontSize: 13, opacity: 0.75 }}>Last run: {lastRunAt}</span>
                    ) : null}
                </div>
            </div>

            {loading ? (
                <div
                    style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 8,
                        padding: 16,
                        background: "#fff",
                        marginBottom: 16,
                    }}
                >
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Progress</div>
                    <div style={{ fontSize: 14, marginBottom: 8 }}>
                        {progressCurrent} of {progressTotal} completed ({progressPercent}%)
                    </div>
                    <div
                        style={{
                            width: "100%",
                            height: 16,
                            background: "#e5e7eb",
                            borderRadius: 999,
                            overflow: "hidden",
                            marginBottom: 8,
                        }}
                    >
                        <div
                            style={{
                                width: `${progressPercent}%`,
                                height: "100%",
                                background: "#155e75",
                                transition: "width 0.2s ease",
                            }}
                        />
                    </div>
                    <div style={{ fontSize: 13, color: "#4b5563" }}>
                        {currentCourse ? `Currently checking: ${currentCourse}` : "Preparing audit..."}
                    </div>
                </div>
            ) : null}

            {error ? <div className="bb-error" style={{ marginBottom: 16 }}>{error}</div> : null}

            {results.length > 0 ? (
                <div style={{ marginBottom: 16, display: "flex", gap: 16, flexWrap: "wrap" }}>
                    <div className="bb-stat-card">
                        <div className="bb-stat-label">Phase</div>
                        <div className="bb-stat-value" style={{ fontSize: 20 }}>{phase}</div>
                    </div>
                    <div className="bb-stat-card">
                        <div className="bb-stat-label">Courses Checked</div>
                        <div className="bb-stat-value">{summary.total}</div>
                    </div>
                    <div className="bb-stat-card">
                        <div className="bb-stat-label">Resolved</div>
                        <div className="bb-stat-value">{summary.resolved}</div>
                    </div>
                    <div className="bb-stat-card">
                        <div className="bb-stat-label">With Overall Grade</div>
                        <div className="bb-stat-value">{summary.withOverallGrade}</div>
                    </div>
                    <div className="bb-stat-card">
                        <div className="bb-stat-label">Using Learning Plans</div>
                        <div className="bb-stat-value">{summary.withLessonPlans}</div>
                    </div>
                    <div className="bb-stat-card">
                        <div className="bb-stat-label">Using Rubrics</div>
                        <div className="bb-stat-value">{summary.withRubrics}</div>
                    </div>
                    <div className="bb-stat-card">
                        <div className="bb-stat-label">Using Video Studio</div>
                        <div className="bb-stat-value">{summary.withVideoStudio}</div>
                    </div>
                    <div className="bb-stat-card">
                        <div className="bb-stat-label">Using Third Party Tools</div>
                        <div className="bb-stat-value">{summary.withThirdPartyTools}</div>
                    </div>
                    <div className="bb-stat-card">
                        <div className="bb-stat-label">With Gradable Items</div>
                        <div className="bb-stat-value">{summary.withGradableItems}</div>
                    </div>
                    <div className="bb-stat-card">
                        <div className="bb-stat-label">Created from Copy</div>
                        <div className="bb-stat-value">{summary.createdFromCopyCount}</div>
                    </div>
                    <div className="bb-stat-card">
                        <div className="bb-stat-label">IH Used</div>
                        <div className="bb-stat-value">{summary.ihUsedCount}</div>
                    </div>
                    <div className="bb-stat-card">
                        <div className="bb-stat-label">Ally Enabled</div>
                        <div className="bb-stat-value">{summary.allyUsedCount}</div>
                    </div>
                    <div className="bb-stat-card">
                        <div className="bb-stat-label">Ally Used in Course</div>
                        <div className="bb-stat-value">{summary.allyUsedInCourseCount}</div>
                    </div>
                    <div className="bb-stat-card">
                        <div className="bb-stat-label">Total Content Items</div>
                        <div className="bb-stat-value">{summary.totalContentItems}</div>
                    </div>
                </div>
            ) : null}

            {showScorecard && results.length > 0 ? (
                <div style={{ marginBottom: 16 }}>
                    <h2 style={{ margin: "0 0 6px 0", fontSize: 20 }}>{phase} Scorecard</h2>
                    <div style={{ marginBottom: 12, fontSize: 14, color: "#4b5563" }}>
                        Selected Phase: <strong>{phase}</strong>
                    </div>

                    {orderedSectionNames.map((sectionName) => {
                        const rows = groupedScorecardSections[sectionName];
                        return (
                            <div key={sectionName} style={{ marginBottom: 20 }}>
                                <h3 style={{ margin: "0 0 10px 0", fontSize: 18 }}>{sectionName}</h3>

                                <table className="bb-table" style={{ marginBottom: 12 }}>
                                    <thead>
                                    <tr>
                                        <th style={{ width: "10%" }}>Applicable</th>
                                        <th style={{ width: "20%" }}>Measured Item</th>
                                        <th style={{ width: "16%" }}>Auto Value</th>
                                        <th style={{ width: "10%" }}>Override %</th>
                                        <th style={{ width: "10%" }}>Effective %</th>
                                        <th style={{ width: "8%" }}>0 — Not in Place</th>
                                        <th style={{ width: "8%" }}>1 — Inconsistent</th>
                                        <th style={{ width: "9%" }}>2 — Mostly Consistent</th>
                                        <th style={{ width: "9%" }}>3 — Standardized</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {rows.map((row) => (
                                        <tr key={row.key}>
                                            <td>
                                                <select
                                                    value={row.applicable}
                                                    onChange={(e) =>
                                                        handleApplicabilityChange(row.key, e.target.value)
                                                    }
                                                    style={{
                                                        width: 80,
                                                        border: "1px solid #d1d5db",
                                                        borderRadius: 6,
                                                        padding: "6px 8px",
                                                        fontSize: 13,
                                                        background: "#fff",
                                                    }}
                                                >
                                                    <option value="Yes">Yes</option>
                                                    <option value="No">No</option>
                                                </select>
                                            </td>
                                            <td>{row.label}</td>
                                            <td>
                                                {row.autoComputed
                                                    ? `${row.autoNumerator} / ${row.autoDenominator} (${row.autoPercentage.toFixed(1)}%)`
                                                    : "Manual only"}
                                            </td>
                                            <td>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    step="0.1"
                                                    value={row.overrideValue}
                                                    onChange={(e) => handleOverrideChange(row.key, e.target.value)}
                                                    placeholder="auto"
                                                    style={{
                                                        width: 90,
                                                        border: "1px solid #d1d5db",
                                                        borderRadius: 6,
                                                        padding: "6px 8px",
                                                        fontSize: 13,
                                                    }}
                                                />
                                            </td>
                                            <td>{row.effectivePercentage.toFixed(1)}%</td>
                                            <td>{row.effectiveScore === 0 ? "●" : ""}</td>
                                            <td>{row.effectiveScore === 1 ? "●" : ""}</td>
                                            <td>{row.effectiveScore === 2 ? "●" : ""}</td>
                                            <td>{row.effectiveScore === 3 ? "●" : ""}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })}
                </div>
            ) : null}

            <table className="bb-table">
                <thead>
                <tr>
                    <th>Input</th>
                    <th>Course ID</th>
                    <th>Course Name</th>
                    <th>Overall Grade</th>
                    <th>Gradable Columns</th>
                    <th>Lesson Plans</th>
                    <th>Rubrics</th>
                    <th>Video Studio</th>
                    <th>Third Party Tools</th>
                    <th>Created from Copy</th>
                    <th>IH Used</th>
                    <th>Ally Enabled</th>
                    <th>Ally Used in Course</th>
                    <th>Content Items</th>
                    <th>Status</th>
                </tr>
                </thead>
                <tbody>
                {results.length === 0 ? (
                    <tr>
                        <td colSpan={15}>Generate or upload courses, then click Check Courses.</td>
                    </tr>
                ) : (
                    results.map((row, idx) => (
                        <tr key={`${row.input}-${idx}`}>
                            <td>{row.input}</td>
                            <td>{row.courseId}</td>
                            <td>{row.courseName}</td>
                            <td>{row.hasOverallGrade}</td>
                            <td>{row.gradableColumnCount}</td>
                            <td>{row.usesLessonPlans}</td>
                            <td>{row.rubricsUsed}</td>
                            <td>{row.videoStudioUsed}</td>
                            <td>{row.thirdPartyToolsUsed}</td>
                            <td>{row.createdFromCopy}</td>
                            <td>{row.ihUsed}</td>
                            <td>{row.allyUsed}</td>
                            <td>{row.allyUsedInCourse}</td>
                            <td>{row.contentItemCount}</td>
                            <td>{row.status}</td>
                        </tr>
                    ))
                )}
                </tbody>
            </table>

            <style>{`
                .bb-container {
                    padding: 16px;
                    font-family: Arial, sans-serif;
                    color: #111827;
                    background: #f9fafb;
                }
                .bb-btn {
                    border: 1px solid #cbd5e1;
                    background: #ffffff;
                    border-radius: 8px;
                    padding: 10px 14px;
                    cursor: pointer;
                    font-size: 14px;
                }
                .bb-btn:disabled {
                    opacity: 0.6;
                    cursor: default;
                }
                .bb-table {
                    width: 100%;
                    border-collapse: collapse;
                    background: #fff;
                }
                .bb-table th {
                    text-align: left;
                    background: #155e75;
                    color: #fff;
                    padding: 12px;
                    border: 1px solid #d1d5db;
                    font-size: 14px;
                }
                .bb-table td {
                    padding: 12px;
                    border: 1px solid #d1d5db;
                    vertical-align: top;
                    font-size: 14px;
                }
                .bb-error {
                    color: #b91c1c;
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    border-radius: 8px;
                    padding: 12px;
                }
                .bb-stat-card {
                    background: #fff;
                    border: 1px solid #d1d5db;
                    border-radius: 8px;
                    padding: 12px 16px;
                    min-width: 180px;
                }
                .bb-stat-label {
                    font-size: 12px;
                    color: #4b5563;
                    margin-bottom: 4px;
                }
                .bb-stat-value {
                    font-size: 24px;
                    font-weight: 700;
                }
            `}</style>
        </div>
    );
};

export default BlackboardAdoptionIndexTool;